import { useState, useEffect } from 'react';
import { FileText, Download, TrendingUp, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import apiClient from '@/utils/apiClient';
import downloadFile from '@/utils/fileDownloader';
import { toast } from 'sonner';
import useParentData from '@/hooks/useParentData';
import useHostelSettings from '@/hooks/useHostelSettings';
import ChildSelector from './ChildSelector';

const MarkSheetsView = () => {
  const { children, selectedChild, setSelectedChild, loading } = useParentData();
  const { settings, logoUrl } = useHostelSettings();
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (selectedChild) {
      fetchResults(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchResults = async (studentId) => {
    setLoadingData(true);
    try {
      const response = await apiClient.get(`/v1/parent/child/${studentId}/results`);
      setResults(response.data.results || []);
    } catch (error) {
      toast.error('Failed to load results');
    } finally {
      setLoadingData(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const calculatePercentage = (obtained, total) => {
    if (!total || total === 0) return 0;
    return ((obtained / total) * 100).toFixed(1);
  };

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-blue-600';
    if (percentage >= 60) return 'text-yellow-600';
    if (percentage >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const handleViewDetails = (result) => {
    setSelectedResult(result);
    setShowDetailModal(true);
  };

  const handlePrintMarksheet = (result) => {
    const targetResult = result || selectedResult;
    if (result) {
      setSelectedResult(result);
    }
    
    const isMobile = Boolean(
      window.DormCircleNative ||
      window.isDormCircleMobileApp ||
      (navigator.userAgent && navigator.userAgent.includes('DormCircleApp'))
    );

    if (isMobile && targetResult) {
      // Export styled HTML marksheet to file for native mobile storage
      const studentName = (selectedChild?.name || 'Student').replace(/\s+/g, '_');
      const examName = (targetResult.exam_type || 'Exam').replace(/\s+/g, '_');
      const fileName = `Marksheet_${studentName}_${examName}.html`;
      
      const el = document.getElementById('printable-marksheet');
      const content = el ? el.outerHTML : `<h2>Marksheet - ${targetResult.exam_type}</h2>`;
      const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${fileName}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 20px; color: #1e293b; background: #f8fafc; }
    .marksheet-wrap { max-width: 800px; margin: 0 auto; background: #fff; padding: 30px; border-radius: 12px; border: 1px solid #cbd5e1; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th { background: #2563eb; color: #fff; padding: 10px; font-size: 12px; text-transform: uppercase; text-align: left; }
    td { padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
    .no-print { display: none !important; }
  </style>
</head>
<body>
  <div class="marksheet-wrap">
    ${content}
  </div>
</body>
</html>`;
      downloadFile(fullHtml, fileName, 'text/html');
      return;
    }

    if (result) {
      setShowDetailModal(true);
      setTimeout(() => {
        window.print();
      }, 250);
    } else {
      window.print();
    }
  };

  if (loading || loadingData) {
    return <div className="p-6 text-center text-gray-500">Loading results...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-marksheet, #printable-marksheet * {
            visibility: visible;
          }
          #printable-marksheet {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 16px;
            background: white !important;
            border: 1px solid #cbd5e1 !important;
            box-shadow: none !important;
            z-index: 999999;
          }
          .no-print {
            display: none !important;
          }
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
        }
      `}</style>

      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>Mark Sheets</h1>
        <p className="text-gray-600 mt-1">View your child's exam results and performance</p>
      </div>

      <ChildSelector 
        children={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
      />

      {/* Results List */}
      <Card>
        <CardHeader>
          <CardTitle>Exam Results</CardTitle>
        </CardHeader>
        <CardContent>
          {results.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No exam results available yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((result, index) => {
                const totalObtained = result?.total_obtained ?? result.subjects?.reduce((sum, subj) => sum + (subj.marks ?? subj.marks_obtained ?? 0), 0);
                const totalMax = result?.total_max ?? result.subjects?.reduce((sum, subj) => sum + (subj.max_marks ?? subj.total_marks ?? 0), 0);
                const percentage = result?.overall_percentage ?? calculatePercentage(totalObtained, totalMax);

                return (
                  <div 
                    key={index} 
                    className="p-4 bg-gray-50 rounded-lg hover:bg-blue-50/50 border border-gray-100 hover:border-blue-200 cursor-pointer transition-all"
                    onClick={() => handleViewDetails(result)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                          <FileText className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-800">{result.exam_type}</h3>
                          <p className="text-sm text-gray-500">Date: {formatDate(result.exam_date)}</p>
                          <p className="text-xs text-gray-400 mt-0.5">Subjects: {result.subjects?.length || 0}</p>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end mb-1">
                          <span className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
                            {totalObtained}
                          </span>
                          <span className="text-gray-400">/</span>
                          <span className="text-xl text-gray-600">{totalMax}</span>
                        </div>
                        <div className={`text-lg font-bold ${getGradeColor(percentage)}`}>
                          {percentage}%
                        </div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Grade: {result.grade} | {result.status}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200/60 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {result.global_stats?.class_highest_percentage !== undefined && result.global_stats?.class_highest_percentage !== null && (
                          <div className="flex items-center gap-1.5 text-xs font-semibold px-2 py-1 bg-yellow-100 text-yellow-700 rounded-md">
                            <Award className="w-3.5 h-3.5" />
                            Class Highest: {result.global_stats.class_highest_percentage}%
                          </div>
                        )}
                        {result.rank && (
                          <div className="flex items-center gap-1.5 text-xs font-semibold px-2 py-1 bg-blue-100 text-blue-700 rounded-md">
                            <TrendingUp className="w-3.5 h-3.5" />
                            Rank: #{result.rank}
                          </div>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-xs font-semibold text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrintMarksheet(result);
                        }}
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download Marksheet
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Digital Marksheet Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-bold" style={{ color: '#2C5F7F' }}>
                    Digital Marksheet
                  </DialogTitle>
                  <p className="text-sm text-gray-500">
                    {selectedResult?.exam_type}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePrintMarksheet()}
                className="gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50 no-print"
              >
                <Download className="w-4 h-4" />
                Print Marksheet
              </Button>
            </div>
          </DialogHeader>

          {selectedResult && (
            <div className="space-y-6 pt-4">
              {/* Marksheet Layout */}
              <div id="printable-marksheet" className="border-2 border-blue-100 rounded-xl p-6 bg-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 opacity-50 z-0 no-print" />

                {/* School/Hostel Header */}
                <div className="flex items-center justify-between border-b pb-4 mb-6">
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded" />
                    ) : (
                      <div className="p-2.5 bg-blue-100 rounded-lg text-blue-600">
                        <Award className="w-5 h-5" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-lg font-bold text-gray-800">
                        {settings?.name || selectedChild?.hostel_name || 'Academic Institution'}
                      </h2>
                      <p className="text-xs text-gray-500">
                        Student Progress Report &bull; {selectedResult.exam_type}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="px-2.5 py-1 bg-blue-50 border border-blue-200 text-blue-700 font-bold text-xs uppercase tracking-wider rounded">
                      Digital Marksheet
                    </span>
                  </div>
                </div>

                {/* Header Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 relative z-10">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Student Name</p>
                    <p className="text-sm font-bold text-gray-700">{selectedChild?.name || 'Student'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Student ID</p>
                    <p className="text-sm font-bold text-gray-700">{selectedChild?.readable_id || selectedChild?.id || '-'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Class</p>
                    <p className="text-sm font-bold text-gray-700">{selectedChild?.class_name || '-'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Exam Date</p>
                    <p className="text-sm font-bold text-gray-700">{formatDate(selectedResult.exam_date)}</p>
                  </div>
                </div>

                {/* Subject Table */}
                <div className="overflow-hidden rounded-lg border border-gray-200 mb-8 relative z-10">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-blue-600 text-white uppercase text-[10px] tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Subject Name</th>
                        <th className="px-4 py-3 text-center">Marks Obtained</th>
                        <th className="px-4 py-3 text-center">Max Marks</th>
                        <th className="px-4 py-3 text-center">Class Average</th>
                        <th className="px-4 py-3 text-right">Percentage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedResult.subjects?.map((sub, idx) => {
                        const marksObtained = sub.marks_obtained ?? sub.marks ?? 0;
                        const totalMarks = sub.total_marks ?? sub.max_marks ?? 100;
                        const pct = sub.percentage !== undefined ? sub.percentage : (totalMarks > 0 ? ((marksObtained / totalMarks) * 100).toFixed(1) : 0);

                        const classAvg = (() => {
                          const stats = selectedResult.global_stats?.subject_averages;
                          if (!stats) return 'N/A';
                          const subName = (sub.subject_name || sub.subject || '')?.toString().toLowerCase().trim();
                          if (!subName) return 'N/A';
                          const directKey = Object.keys(stats).find(k => k?.toString().toLowerCase().trim() === subName);
                          if (directKey && stats[directKey]?.average !== undefined && stats[directKey]?.average !== null) {
                            return stats[directKey].average;
                          }
                          const fuzzyKey = Object.keys(stats).find(k => {
                            const kNorm = k?.toString().toLowerCase().trim();
                            return kNorm && (kNorm.startsWith(subName) || subName.startsWith(kNorm));
                          });
                          if (fuzzyKey && stats[fuzzyKey]?.average !== undefined && stats[fuzzyKey]?.average !== null) {
                            return stats[fuzzyKey].average;
                          }
                          return 'N/A';
                        })();

                        return (
                          <tr key={idx} className="hover:bg-blue-50 transition-colors">
                            <td className="px-4 py-3 font-semibold text-gray-700">{sub.subject_name || sub.subject}</td>
                            <td className="px-4 py-3 text-center font-bold text-blue-600">{marksObtained}</td>
                            <td className="px-4 py-3 text-center text-gray-500">{totalMarks}</td>
                            <td className="px-4 py-3 text-center font-semibold text-gray-600">{classAvg}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-bold ${pct >= 40 ? 'text-green-600' : 'text-red-600'}`}>
                                {pct}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 font-bold border-t-2 border-blue-100">
                      <tr>
                        <td className="px-4 py-4 text-blue-600">GRAND TOTAL</td>
                        <td className="px-4 py-4 text-center text-blue-600 text-lg">
                          {selectedResult.total_obtained}
                        </td>
                        <td className="px-4 py-4 text-center text-gray-500">
                          {selectedResult.total_max}
                        </td>
                        <td className="px-4 py-4 text-center text-gray-400 font-normal">
                          -
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className={`text-lg ${selectedResult.overall_percentage >= 40 ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedResult.overall_percentage}%
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Performance Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
                  <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Overall Grade</p>
                    <p className="text-3xl font-black text-blue-600">{selectedResult.grade}</p>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Pass Status</p>
                    <p className={`text-2xl font-black ${selectedResult.status === 'PASSED' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedResult.status}
                    </p>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                    <p className="text-[10px] font-bold text-yellow-500 uppercase mb-1">Class Highest</p>
                    <p className="text-3xl font-black text-yellow-600">
                      {selectedResult.global_stats?.class_highest_percentage !== undefined && selectedResult.global_stats?.class_highest_percentage !== null
                        ? `${selectedResult.global_stats.class_highest_percentage}%`
                        : 'N/A'}
                    </p>
                  </div>
                  {selectedResult.rank ? (
                    <div className="flex flex-col items-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                      <p className="text-[10px] font-bold text-purple-400 uppercase mb-1">Class Rank</p>
                      <p className="text-3xl font-black text-purple-600">#{selectedResult.rank}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Remarks</p>
                      <p className="text-sm font-medium text-gray-600 text-center italic">
                        {selectedResult.remarks || (
                          selectedResult.overall_percentage >= 75 ? "Excellent Performance!" : 
                          selectedResult.overall_percentage >= 60 ? "Good Job, keep improving." : 
                          selectedResult.overall_percentage >= 40 ? "Can do better." : "Needs hard work."
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Grading Scheme Reference */}
              <div className="bg-gray-50/50 border border-gray-200/65 rounded-xl p-4 shadow-none">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5 uppercase tracking-wider mb-2.5">
                  <Award className="w-3.5 h-3.5 text-blue-500" />
                  Grading & Evaluation Scheme Reference
                </p>
                <div className="grid grid-cols-4 md:grid-cols-7 gap-1 text-center font-semibold text-[11px] text-gray-500">
                  <div className="p-1 bg-green-50 text-green-700 rounded border border-green-100">
                    <span className="block font-bold">A+</span> &ge;90%
                  </div>
                  <div className="p-1 bg-blue-50 text-blue-700 rounded border border-blue-100">
                    <span className="block font-bold">A</span> 80-89%
                  </div>
                  <div className="p-1 bg-indigo-50 text-indigo-700 rounded border border-indigo-100">
                    <span className="block font-bold">B</span> 70-79%
                  </div>
                  <div className="p-1 bg-yellow-50 text-yellow-700 rounded border border-yellow-100">
                    <span className="block font-bold">C</span> 60-69%
                  </div>
                  <div className="p-1 bg-orange-50 text-orange-700 rounded border border-orange-100">
                    <span className="block font-bold">D</span> 50-59%
                  </div>
                  <div className="p-1 bg-amber-50/70 text-amber-700 rounded border border-amber-100">
                    <span className="block font-bold">E</span> 40-49%
                  </div>
                  <div className="p-1 bg-red-50 text-red-700 rounded border border-red-100">
                    <span className="block font-bold">F</span> &lt;40%
                  </div>
                </div>
                <p className="text-[10px] text-center italic text-gray-400 mt-2">
                  * Minimum passing criteria is <strong>33%</strong>. Overall Grade is calculated on the cumulative percentage of all subjects.
                </p>
              </div>

              {/* Footer Actions */}
              <div className="flex justify-end gap-3 no-print">
                <Button variant="outline" onClick={() => handlePrintMarksheet()} className="gap-2">
                  <Download className="w-4 h-4" />
                  Print Marksheet
                </Button>
                <Button 
                  onClick={() => setShowDetailModal(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarkSheetsView;
